/**
 * Frontend client for the HTTP Automation "API Connections" admin API
 * (/admin/api-connections). Mirrors the backend sanitized shapes — secrets are
 * never returned, only `hasX` presence flags.
 */

import api from './api.service';

export type ServiceAuthType =
  | 'none'
  | 'bearer'
  | 'basic'
  | 'api_key_header'
  | 'hmac'
  | 'oauth2_client_credentials'
  /** Signed with a registered External API Client's secret; host-locked to its callback URL. */
  | 'external_client';

export type RequiresAuthLevel = 'none' | 'identified' | 'verified';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export type BodyMode = 'none' | 'json' | 'form' | 'raw';

/** Where a mapped response value goes. */
export type VariableTarget =
  | { kind: 'flow'; name: string }
  | { kind: 'contactField'; key: string }
  | { kind: 'leadField'; key: string };

export interface ResponseMapping {
  /** Dot/bracket path into the JSON response, e.g. `data[0].gender`. */
  source: string;
  target: VariableTarget;
}

/** The saved request of an API + how its response is mapped. */
export interface ApiRequestDef {
  method: HttpMethod;
  path?: string;
  queryParams?: Array<{ key: string; value: string }>;
  headers?: Array<{ key: string; value: string }>;
  bodyMode?: BodyMode;
  body?: string;
  responseMappings?: ResponseMapping[];
}

export interface ApiConnection {
  id: string;
  name: string;
  description?: string;
  baseUrl: string;
  defaultHeaders?: Record<string, string>;
  auth: {
    type: ServiceAuthType;
    apiKeyHeaderName?: string;
    externalClientId?: string;
    hasCredential: boolean;
    hasHmacSecret: boolean;
    hasUserContextSecret: boolean;
    oauth2?: { tokenUrl?: string; scopes?: string[]; hasClientId: boolean; hasClientSecret: boolean };
  };
  requiresAuthLevel: RequiresAuthLevel;
  request?: ApiRequestDef;
  sendUserContext: boolean;
  allowedHosts?: string[];
  timeoutMs?: number;
  rateLimit?: { maxConcurrent: number; rps: number };
  status: 'active' | 'disabled';
  createdAt: string;
  updatedAt: string;
}

/** Create/update payload. Secrets are plaintext here (write-only); omit on an
 *  update to keep the stored value. */
export interface ApiConnectionInput {
  name?: string;
  description?: string;
  baseUrl?: string;
  defaultHeaders?: Record<string, string>;
  auth?: {
    type: ServiceAuthType;
    credential?: string;
    apiKeyHeaderName?: string;
    hmacSecret?: string;
    userContextSecret?: string;
    oauth2?: { tokenUrl?: string; clientId?: string; clientSecret?: string; scopes?: string[] };
    externalClientId?: string;
  };
  requiresAuthLevel?: RequiresAuthLevel;
  request?: ApiRequestDef;
  sendUserContext?: boolean;
  allowedHosts?: string[];
  timeoutMs?: number;
  rateLimit?: { maxConcurrent: number; rps: number };
  status?: 'active' | 'disabled';
}

export interface TestResult {
  ok: boolean;
  status: number;
  body: string;
  /** Parsed JSON body when the response was JSON — drives click-to-map. */
  json?: unknown;
  durationMs: number;
  error?: string;
  blocked?: boolean;
  mapped?: Record<string, unknown>;
  variables?: Record<string, unknown>;
}

/** Payload for a dry run. `sampleVariables` fill the {{vars}} in the request. */
export interface TestPayload {
  request: ApiRequestDef;
  sampleVariables?: Record<string, unknown>;
  /** Simulated end-user identity for the dry run — mirrors what a live
   *  conversation supplies (the verified WhatsApp number + its auth level). The
   *  gate checks `authLevel`; `sub` becomes the signed X-Baatly-User-Context the
   *  partner API reads. Omitted → the engine sees auth level 'none'. */
  userContext?: { sub: string; channel?: string; authLevel: RequiresAuthLevel };
}

const BASE = '/admin/api-connections';

// The shared `api` client unwraps the axios response — api.get<T>() resolves to
// the response body T directly — so T is the `{ success, data }` envelope and the
// payload is `.data`.
type Env<T> = { success: boolean; data: T };

class ApiConnectionService {
  async list(): Promise<ApiConnection[]> {
    const res = await api.get<Env<ApiConnection[]>>(BASE);
    return res.data;
  }

  async get(id: string): Promise<ApiConnection> {
    const res = await api.get<Env<ApiConnection>>(`${BASE}/${id}`);
    return res.data;
  }

  async create(input: ApiConnectionInput): Promise<ApiConnection> {
    const res = await api.post<Env<ApiConnection>>(BASE, input);
    return res.data;
  }

  async update(id: string, input: ApiConnectionInput): Promise<ApiConnection> {
    const res = await api.patch<Env<ApiConnection>>(`${BASE}/${id}`, input);
    return res.data;
  }

  async remove(id: string): Promise<void> {
    await api.delete(`${BASE}/${id}`);
  }

  /** Test an UNSAVED API exactly as the form describes it (real call). */
  async testDraft(
    draft: ApiConnectionInput & { baseUrl: string },
    payload: TestPayload
  ): Promise<TestResult> {
    const res = await api.post<Env<TestResult>>(`${BASE}/test`, { ...draft, ...payload });
    return res.data;
  }

  /** Test an existing API with the form's request; saved credentials are used.
   *  Pass `baseUrl` to test an edited URL without saving first. */
  async testSaved(id: string, payload: TestPayload, baseUrl?: string): Promise<TestResult> {
    const res = await api.post<Env<TestResult>>(`${BASE}/${id}/test`, {
      ...payload,
      ...(baseUrl ? { baseUrl } : {}),
    });
    return res.data;
  }
}

export default new ApiConnectionService();
